// REQ-088 (Fase 2 del plan de Reportería) — Tab "Demográfico": distribución de reconocimientos
// por banda generacional (Boomer/Gen X/Millennial/Gen Z/Gen Alpha). La API ya agrupa por banda y
// devuelve el nombre de la banda directamente en `nombre` (no un id) — a diferencia de los demás
// tabs, GetRecognitionGenerationalQueryHandler no tiene el bug de nombres crudos, así que no hace
// falta resolver nada en el front. SIN puntos.
//
// RF-152 — filtros propios BU/Depto/Líderes-Pares, en estado local del tab (independientes
// del FilterBar compartido: la OU/fecha de `params` siguen aplicando igual que antes).
//
// 2026-09-24 — el Site DEJÓ de ser local: subió a la barra principal de Reportería (visible en
// Diario, Brillamos y Demográfico) y llega aquí como `params.site`. Tener dos selectores de Site en la
// misma pantalla —el de la barra y el de este panel— dejaba abierta la pregunta de cuál manda.
//
// C5/C7 (2026-08-13, BREAKING) — el endpoint dejó de devolver un array plano: ahora envuelve la
// distribución (`bandas`) junto con `ousConCobertura` (ids de OU cuya cobertura de fecha de
// nacimiento alcanzó para aportar al menos una persona con banda resuelta). Se pinta como un
// marcado visual aparte —no forma parte del ranking— porque responde una pregunta distinta:
// "¿el cálculo tuvo datos de ESTA unidad?", no "cuánto reconoció esta banda".
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessUnits, useRecognitionGenerational } from '@/api/recognition';
import type { RecognitionReportParams, GenerationalFilters } from '@/api/recognition';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { ReportStatus } from '../reportsShared';
import { DistribucionGeneracional } from './DistribucionGeneracional';

/**
 * `ouIds` — 2026-10-06: las OUs ELEGIDAS en la barra (sin expandir). El selector «BU» solo ofrece las
 * BUs de esas OUs, igual que el de Site; sin OU elegida, las del alcance completo.
 */
export function GenerationalTab(
  { params, ouIds = [] }: Readonly<{ params: RecognitionReportParams; ouIds?: readonly string[] }>,
) {
  const { t } = useTranslation('recognition');
  const [buElegida, setBu] = useState('');
  const [depto, setDepto] = useState('');
  const [lideresOPares, setLideresOPares] = useState<GenerationalFilters['lideresOPares'] | ''>('');
  const { resolveOuName } = useOuNameResolver();
  // DEM-F2 (2026-09-28) — la persona ya no llega a esta pestaña: la barra oculta su buscador aquí y
  // `RecognitionAnalyticsView` no la manda. Antes se avisaba con `AvisoFiltroNoAplica` («porBanda»).

  // 2026-10-06 — la BU dejó de ser texto libre: es un selector con las BUs de la OU elegida. Si la OU
  // cambia y la BU elegida ya no está en la lista, se deja de aplicar en el mismo render (derivado, no
  // con un efecto: un efecto correría después y alcanzaría a pedir datos con una BU de otra OU).
  const busQuery = useBusinessUnits(ouIds);
  const bus = busQuery.data ?? [];
  const bu = bus.some((opcion) => opcion.id === buElegida) ? buElegida : '';

  const filters: GenerationalFilters = {
    bu: bu || undefined,
    depto: depto || undefined,
    site: params.site || undefined,
    lideresOPares: lideresOPares || undefined,
  };

  const q = useRecognitionGenerational(params, filters);
  const data = q.data?.bandas ?? [];
  const ousConCobertura = q.data?.ousConCobertura ?? [];
  const isEmpty = !!q.data && data.length === 0;

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-people-group" aria-hidden="true" /> {t('reports.generational.title')}
          </div>
          <div className="au-card-sub">{t('reports.generational.subtitle')}</div>
        </div>
      </div>

      <div className="rr-actions">
        <select
          className="au-cascade-select"
          value={bu}
          onChange={(e) => setBu(e.target.value)}
          disabled={busQuery.isLoading || bus.length === 0}
          aria-label={t('reports.generational.filterBu')}
        >
          <option value="">{t('reports.generational.buAll')}</option>
          {bus.map((opcion) => (
            <option key={opcion.id} value={opcion.id}>{opcion.name}</option>
          ))}
        </select>
        <input
          type="text"
          className="au-date-input"
          placeholder={t('reports.generational.filterDepto')}
          value={depto}
          onChange={(e) => setDepto(e.target.value)}
          aria-label={t('reports.generational.filterDepto')}
        />
        <select
          className="au-cascade-select"
          value={lideresOPares}
          onChange={(e) => setLideresOPares(e.target.value as GenerationalFilters['lideresOPares'] | '')}
          aria-label={t('reports.generational.roleAll')}
        >
          <option value="">{t('reports.generational.roleAll')}</option>
          <option value="lideres">{t('reports.generational.roleLeaders')}</option>
          <option value="pares">{t('reports.generational.rolePeers')}</option>
        </select>
      </div>

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {/* T-11 — las tarjetas con color de la maqueta (rep-panel-demografico) SUSTITUYEN a la
            `RankedList` de barras planas.
            
            Se probó apilando las dos y el nombre de cada banda salía DUPLICADO en pantalla. No se
            pierde nada al quitarla: la tarjeta trae el conteo exacto además del porcentaje, el rango
            de años y el color. Y el prototipo es la autoridad de diseño (ver `decisiones.md`), que
            dibuja solo tarjetas. */}
        <DistribucionGeneracional bandas={data} />

        {/* C5/C7 — qué OUs SÍ aportaron cobertura de fecha de nacimiento al cálculo. Ausente de
            esta lista no es "sin actividad": es "sin datos de nacimiento confiables". */}
        {ousConCobertura.length > 0 && (
          <div className="rr-actions--top-gap">
            <div className="rr-block-title--tight">{t('reports.generational.coverageTitle')}</div>
            <div className="rr-coverage-list">
              {ousConCobertura.map((ouId) => (
                <span key={ouId} className="rr-coverage-chip">
                  <i className="fas fa-circle-check" aria-hidden="true" /> {resolveOuName(ouId)}
                </span>
              ))}
            </div>
          </div>
        )}
      </ReportStatus>
    </div>
  );
}
